import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import MultiUserSelect from "@/components/MultiUserSelect";

export const PROJECT_STATUSES = [
  { value: "to_start",     label: "To Start",    dot: "bg-slate-400",  chip: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "progressing",  label: "Progressing", dot: "bg-blue-500",   chip: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "on_hold",      label: "On Hold",     dot: "bg-amber-500",  chip: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "stopped",      label: "Stopped",     dot: "bg-red-500",    chip: "bg-red-50 text-red-700 border-red-200" },
];

/**
 * EditProjectDialog — dual-purpose project form dialog.
 *
 * Renders inputs that match the columns visible in the Projects tab
 * (name, site URL, status, status note, TLs, CMs, writers, publishers, editors)
 * plus a department picker when creating (defaults to "content" on the backend).
 *
 * Modes:
 *   - "edit"   (default) — `project` prop required; PATCH /projects/:id on save.
 *   - "create"           — POST /projects on save; role-gated field visibility.
 */
export default function EditProjectDialog({ open, onClose, project, users, currentUser, onSaved, mode = "edit", defaults }) {
  const isCreate = mode === "create";
  const [form, setForm] = useState({
    name: "", site_url: "",
    team_lead_ids: [], content_manager_ids: [],
    writer_ids: [], publisher_ids: [], editor_ids: [],
    status: "to_start", status_note: "",
    department: "content",
    // Iter 36.7 Phase 2 — keyword-availability toggle drives the
    // "Projects without keyword" banner. `kw_unassigned=true` means
    // no keyword yet; false means user has confirmed keyword is set.
    kw_unassigned: false,
  });
  const [saving, setSaving] = useState(false);

  // Iter 36.7 Phase 2.10 — persist the create-mode draft to localStorage so
  // switching tabs, navigating away, or accidentally closing the dialog
  // never wipes the user's typing. Restored on next open; cleared on save.
  const DRAFT_KEY = `pluto.newProjectDraft.${currentUser?.id || "anon"}`;

  useEffect(() => {
    // Only run on `open` transitions or when editing a different project.
    // Guarding on `project?.id` (a stable string) instead of the reference
    // avoids resetting the form whenever the parent re-renders (e.g. after
    // a dataBus refresh that reallocates the `currentUser`/`users` refs).
    if (!open) return;
    if (isCreate) {
      let draft = null;
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) draft = JSON.parse(raw);
      } catch (e) { /* corrupt localStorage — ignore */ }
      if (draft && typeof draft === "object" && draft.name !== undefined) {
        setForm(draft);
        return;
      }
      // No draft — start fresh with role-aware defaults. Iter 36.9.11 —
      // callers can pass `defaults` to prefill (e.g. sidebar's "Add project
      // under TL X" pre-loads team_lead_ids and content_manager_ids).
      const selfIsCM = currentUser?.role === "content_manager";
      const selfIsTL = currentUser?.role === "team_lead";
      setForm({
        name: "", site_url: "",
        team_lead_ids: selfIsTL ? [currentUser.id] : [],
        content_manager_ids: selfIsCM ? [currentUser.id] : [],
        writer_ids: [], publisher_ids: [], editor_ids: [],
        status: "to_start", status_note: "",
        department: (currentUser?.departments && currentUser.departments[0]) || currentUser?.department || "content",
        kw_unassigned: false,
        team_id: "",
        ...(defaults || {}),
      });
    } else if (project) {
      setForm({
        name: project.name || "",
        site_url: project.site_url || "",
        team_lead_ids: project.team_lead_ids || [],
        content_manager_ids: project.content_manager_ids || [],
        writer_ids: project.writer_ids || [],
        publisher_ids: project.publisher_ids || [],
        editor_ids: project.editor_ids || [],
        status: project.status || "to_start",
        status_note: project.status_note || "",
        department: project.department || "content",
        kw_unassigned: !!project.kw_unassigned,
        team_id: project.team_id || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id, isCreate]);

  // Save the create-mode draft whenever the form changes. Skipped for edit
  // mode — those go through explicit PATCH on save.
  useEffect(() => {
    if (!open || !isCreate || !currentUser?.id) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch (e) { /* quota — ignore */ }
  }, [form, open, isCreate, currentUser?.id, DRAFT_KEY]);

  // Role checks — mirror backend rules so the UI matches what the API accepts.
  const isAdmin = ["admin", "super_admin", "department_admin"].includes(currentUser?.role);
  const isCM = currentUser?.role === "content_manager";
  const isTL = currentUser?.role === "team_lead";
  const canEditTLs = isAdmin || isCM;
  const canEditCMs = isAdmin;
  const canEditProject = isAdmin || isCM || isTL;

  // Iter 36.7 Phase 2.5 — broadened picker pools. Any visible user can be
  // picked for any project role slot; role tags in the dropdown make it
  // clear who is who. Backend RBAC is enforced at write time.
  //
  // Exception: a Team Lead creating their own project can only pick
  // themselves as TL (backend rule: `if user.role == "team_lead": user.id
  // must be in team_lead_ids`).
  const teamLeadOptions = useMemo(() => {
    if (isTL && isCreate) return [currentUser].filter(Boolean);
    return users;
  }, [users, isTL, isCreate, currentUser]);
  const contentManagerOptions = useMemo(() => users, [users]);
  const associateOptions = useMemo(() => users, [users]);

  // Iter 36.7 Phase 2.7 — Team dropdown now sources from the PERSISTED
  // teams collection so a team keeps appearing even when it has 0 TLs.
  // Fetched once on open. Option value = team row id → written to
  // `form.team_id` and persisted verbatim on save.
  const [teamRows, setTeamRows] = useState([]);
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    api.get("/teams").then((r) => { if (!cancel) setTeamRows(r.data || []); }).catch(() => {});
    return () => { cancel = true; };
  }, [open]);

  const teamOptions = useMemo(() => {
    const selectedCmIds = form.content_manager_ids || [];
    if (selectedCmIds.length === 0) return []; // gate on CM selection
    const cms = users.filter((u) => u.role === "content_manager");
    const cmById = new Map(cms.map((c) => [c.id, c]));
    const tlsByKey = new Map(); // (cm_id, team_name) → [tl_ids]
    for (const u of users) {
      if (u.role !== "team_lead") continue;
      if (!u.team_name || !u.manager_id) continue;
      const k = `${u.manager_id}|${u.team_name}`;
      if (!tlsByKey.has(k)) tlsByKey.set(k, []);
      tlsByKey.get(k).push(u.id);
    }
    return teamRows
      .filter((t) => selectedCmIds.includes(t.cm_id))
      .map((t) => {
        const cm = cmById.get(t.cm_id);
        return {
          value: t.id, // now team row id (was cm_id|name composite)
          label: t.name,
          sub: cm ? `under ${cm.name}` : "",
          cm_id: t.cm_id,
          department: t.department || cm?.department || "content",
          tl_ids: tlsByKey.get(`${t.cm_id}|${t.name}`) || [],
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users, form.content_manager_ids, teamRows]);

  const onPickTeam = (val) => {
    if (val === "__none__") {
      setForm((f) => ({ ...f, team_id: "", team_lead_ids: [] }));
      return;
    }
    const t = teamOptions.find((x) => x.value === val);
    if (!t) return;
    setForm((f) => ({
      ...f,
      team_id: t.value,
      team_lead_ids: t.tl_ids, // auto-fill current TLs (user can still edit)
      content_manager_ids: Array.from(new Set([...(f.content_manager_ids || []), t.cm_id])),
      department: t.department || f.department,
    }));
  };

  // Team value driven by form.team_id — no more inference from TL/CM lists.
  const activeTeamValue = form.team_id || "";

  const save = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    // Iter 36.7 Phase 2 — Team/CM/TL are no longer mandatory at creation.
    // Admins can create bare projects and assign the team later via the
    // Edit dialog or row menu.
    setSaving(true);
    try {
      if (isCreate) {
        const payload = {
          name: form.name.trim(),
          site_url: form.site_url.trim(),
          department: form.department || "content",
          team_lead_ids: form.team_lead_ids,
          status: form.status,
          status_note: form.status_note,
          writer_ids: form.writer_ids,
          publisher_ids: form.publisher_ids,
          editor_ids: form.editor_ids,
          kw_unassigned: !!form.kw_unassigned,
          team_id: form.team_id || undefined,
        };
        if (canEditCMs && form.content_manager_ids?.length) payload.content_manager_ids = form.content_manager_ids;
        const { data } = await api.post("/projects", payload);
        try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
        toast.success("Project created");
        onSaved && await onSaved(data);
        onClose();
        return;
      }
      const patch = {
        name: form.name.trim(),
        site_url: form.site_url.trim(),
        status: form.status,
        status_note: form.status_note,
        writer_ids: form.writer_ids,
        publisher_ids: form.publisher_ids,
        editor_ids: form.editor_ids,
        kw_unassigned: !!form.kw_unassigned,
        team_id: form.team_id || "",
      };
      if (canEditTLs) patch.team_lead_ids = form.team_lead_ids;
      if (canEditCMs) patch.content_manager_ids = form.content_manager_ids;
      await api.patch(`/projects/${project.id}`, patch);
      toast.success("Project updated");
      onSaved && await onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // In edit mode we require a project; in create mode we render regardless.
  if (!isCreate && !project) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={isCreate ? "new-project-dialog" : "edit-project-dialog"}>
        <DialogHeader>
          <DialogTitle>{isCreate ? "New project" : "Edit project"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Project name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isCreate ? "e.g. TechRadar Blog" : undefined} className="mt-1" data-testid={isCreate ? "new-project-name" : "edit-project-name"} />
            </div>
            <div>
              <Label className="text-xs">Site URL</Label>
              <Input value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} placeholder="https://…" className="mt-1" data-testid={isCreate ? "new-project-url" : "edit-project-url"} />
            </div>
          </div>

          {isCreate && isAdmin && (
            <div>
              <Label className="text-xs">Department</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="new-project-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="content">Content</SelectItem>
                  <SelectItem value="seo">SEO</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROJECT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status note <span className="text-slate-400">(optional)</span></Label>
              <Input value={form.status_note} onChange={(e) => setForm({ ...form, status_note: e.target.value })} placeholder="e.g. Awaiting client brief" className="mt-1" data-testid="edit-project-status-note" />
            </div>
          </div>

          {/* Iter 36.7 Phase 2 (revised) — flow: pick Content Manager → pick
              Team → Team Leads auto-fill. Content Managers therefore render
              BEFORE the Team dropdown; the Team dropdown is scoped to the
              currently-selected CM(s). */}
          <div>
            <Label className="text-xs">Content Managers {!canEditCMs && <span className="text-slate-400">(admin-only)</span>}</Label>
            <div className="mt-1">
              <MultiUserSelect
                value={form.content_manager_ids}
                onChange={(v) => setForm({ ...form, content_manager_ids: v })}
                users={contentManagerOptions}
                placeholder="Pick one or more Content Managers first…"
                disabled={!canEditCMs}
                testId="edit-project-cms"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Team Leads {!canEditTLs && <span className="text-slate-400">(read-only)</span>}</Label>
            <div className="mt-1">
              <MultiUserSelect
                value={form.team_lead_ids}
                onChange={(v) => setForm({ ...form, team_lead_ids: v })}
                users={teamLeadOptions}
                placeholder="Auto-fills when you pick a Team above"
                disabled={!canEditTLs}
                testId="edit-project-tls"
              />
            </div>
          </div>

          {/* Iter 36.7 Phase 2 — Keyword availability. When unchecked
              ("Keyword Not Available"), the project shows in the
              "Projects without keyword" banner on Projects tab. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <Label className="text-xs mb-2 block">Keyword status</Label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="kw-status"
                  checked={!form.kw_unassigned}
                  onChange={() => setForm({ ...form, kw_unassigned: false })}
                  className="accent-purple-600"
                  data-testid="edit-project-kw-available"
                />
                <span className="text-slate-700">Keyword Available</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="kw-status"
                  checked={!!form.kw_unassigned}
                  onChange={() => setForm({ ...form, kw_unassigned: true })}
                  className="accent-amber-600"
                  data-testid="edit-project-kw-not-available"
                />
                <span className="text-slate-700">Keyword Not Available</span>
              </label>
            </div>
            {form.kw_unassigned && (
              <p className="text-[11px] text-amber-700 mt-1.5">Shows in the "Projects without keyword" banner until a keyword is added.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Writers</Label>
              <div className="mt-1">
                <MultiUserSelect value={form.writer_ids} onChange={(v) => setForm({ ...form, writer_ids: v })} users={associateOptions} placeholder="Writers…" disabled={!canEditProject} testId="edit-project-writers" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Publishers</Label>
              <div className="mt-1">
                <MultiUserSelect value={form.publisher_ids} onChange={(v) => setForm({ ...form, publisher_ids: v })} users={associateOptions} placeholder="Publishers…" disabled={!canEditProject} testId="edit-project-publishers" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Editors</Label>
              <div className="mt-1">
                <MultiUserSelect value={form.editor_ids} onChange={(v) => setForm({ ...form, editor_ids: v })} users={associateOptions} placeholder="Editors…" disabled={!canEditProject} testId="edit-project-editors" />
              </div>
            </div>
          </div>

          <p className="text-[11px] text-slate-500">Only assigned Team Leads / Content Managers / Writers / Publishers / Editors (and admin) can access this project.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={isCreate ? "new-project-cancel" : "edit-project-cancel"}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid={isCreate ? "new-project-submit" : "edit-project-save"}>
            {saving ? (isCreate ? "Creating…" : "Saving…") : (isCreate ? "Create project" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
